import type { GetBrandDTO, MbcBrief } from "@nlc/shared-types";

import type { MBCReviewData } from "./types";

const EMPTY_BRAND_GUIDELINES: MBCReviewData["brandGuidelines"] = {
  colors: [],
  typography: [],
};

function capitalize(value: string): string {
  return value ? value[0]!.toUpperCase() + value.slice(1) : value;
}

function mapBrandGuidelines(
  brand: GetBrandDTO,
): MBCReviewData["brandGuidelines"] {
  return {
    colors: brand.colorPalette.map((color) => ({
      hex: color.hexCode,
      name: color.purpose ?? "",
      swatch: color.hexCode,
    })),
    typography: brand.typography.map((font) =>
      `${font.family} ${capitalize(font.variant)}`.trim(),
    ),
  };
}

function extractExtension(fileName: string): { ext?: string } {
  const match = fileName.match(/\.([\dA-Za-z]+)$/);

  if (!match) return {};
  const ext = match[1]?.toUpperCase();

  return ext ? { ext } : {};
}

function mapFiles(group: MbcBrief["assets"]): MBCReviewData["assets"]["files"] {
  return (group?.files ?? []).map((file) => ({
    caption: file.name,
    src: file.url,
    ...extractExtension(file.name),
  }));
}

function mapLinks(group: MbcBrief["assets"]): string[] {
  return (group?.links ?? []).map((link) => link.url);
}

export function mapBriefToReview(
  brief: MbcBrief | null,
  brand?: GetBrandDTO | null,
  designTypeName = "",
): MBCReviewData {
  return {
    assets: {
      files: mapFiles(brief?.assets),
      links: mapLinks(brief?.assets),
    },
    brandGuidelines: brand ? mapBrandGuidelines(brand) : EMPTY_BRAND_GUIDELINES,
    brief: {
      fileSizes: (brief?.fileSizes ?? []).map((size) => ({
        label: size.label ?? "",
        size: size.dimensions ?? "",
      })),
      fileTypes: brief?.fileTypes ?? [],
      textForDesign: brief?.textForDesign ?? "",
      title: brief?.title ?? "",
      typeTag: designTypeName,
      visualDirection: brief?.visualDirection ?? "",
    },
    briefSummary: [
      { label: "Brand", value: brand?.name ?? "" },
      { label: "Type", value: designTypeName },
    ],
    visualReferences: {
      images: mapFiles(brief?.inspirations),
      links: mapLinks(brief?.inspirations),
    },
  };
}
